import React, { useState, useEffect } from 'react';
import { Modal, Table, Form, Button, Tabs, Tab } from 'react-bootstrap';
import { insertTableBd, updateTableBd } from '../../config/functions';
import { toast } from 'react-toastify';

const ModalVarSelectorOpt = ({ 
    show, 
    handleClose, 
    selectedVarIndex, 
    availableVariables, 
    currentVarId, 
    currentVariableName,
    currentMin, 
    currentMax, 
    currentAlarmMin,
    currentAlarmMax,
    currentAlarmId,
    rig,
    uid,
    user,
    onSelectVariable,
    onAlarmUpdated,
    initialTab = 'axes', // 🆕 Permite abrir el modal en un tab específico
    theme
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredVariables, setFilteredVariables] = useState([]);
    const [selectedVariable, setSelectedVariable] = useState(null);
    const [minValue, setMinValue] = useState('');
    const [maxValue, setMaxValue] = useState('');
    const [alarmMinValue, setAlarmMinValue] = useState('');
    const [alarmMaxValue, setAlarmMaxValue] = useState('');
    const [activeTab, setActiveTab] = useState(initialTab); // 'axes', 'variable', 'alarm'
    
    // Aplicar tema por defecto si no se proporciona
    const currentTheme = theme || {
        bgColor: '#000000',
        fontColor: '#ffffff',
        chartBorder: '#333333'
    };

    // Obtener la variable actual completa
    const currentVariable = availableVariables?.find(
        v => v.nb_var_idn.toString() === currentVarId?.toString()
    ) || null;

    // Filtrar variables cuando cambie el término de búsqueda o las variables disponibles
    useEffect(() => {
        if (!availableVariables || availableVariables.length === 0) {
            setFilteredVariables([]);
            return;
        }

        if (!searchTerm.trim()) {
            setFilteredVariables(availableVariables);
            return;
        }

        const term = searchTerm.toLowerCase();
        const filtered = availableVariables.filter(variable => {
            const nombre = (variable.vr_var_nom || '').toLowerCase();
            const unidad = (variable.vr_var_umd || '').toLowerCase();
            
            return nombre.includes(term) || unidad.includes(term);
        });

        setFilteredVariables(filtered);
    }, [searchTerm, availableVariables]);

    // Resetear búsqueda al abrir/cerrar modal y cargar valores actuales
    useEffect(() => {
        if (show) {
            setSearchTerm('');
            // Pre-seleccionar la variable actual si existe
            if (currentVariable) {
                setSelectedVariable(currentVariable);
            }
            setMinValue(currentMin?.toString() || '0');
            setMaxValue(currentMax?.toString() || '100');
            setAlarmMinValue(currentAlarmMin?.toString() || '0');
            setAlarmMaxValue(currentAlarmMax?.toString() || '0');
            // Establecer tab inicial según prop
            setActiveTab(initialTab);
        }
    }, [show, currentMin, currentMax, currentAlarmMin, currentAlarmMax, currentVariable, initialTab]);

    // Manejar selección de variable
    const handleVariableClick = (variable) => {
        setSelectedVariable(variable);
        // Si la variable seleccionada es diferente a la actual, usar sus valores por defecto
        if (variable.nb_var_idn.toString() !== currentVarId?.toString()) {
            setMinValue(variable.nb_tldvrb_min?.toString() || '0');
            setMaxValue(variable.nb_tldvrb_max?.toString() || '100');
        } else {
            // Si es la misma, mantener los valores actuales
            setMinValue(currentMin?.toString() || '');
            setMaxValue(currentMax?.toString() || '');
        }
    };

    // Manejar aplicar cambios de ejes/variable
    const handleApply = () => {
        if (!selectedVariable) {
            alert('Por favor selecciona una variable');
            return;
        }

        const min = parseFloat(minValue);
        const max = parseFloat(maxValue);

        // Validar que min < max
        if (isNaN(min) || isNaN(max)) {
            alert('Por favor ingresa valores numéricos válidos para Min y Max');
            return;
        }

        if (min >= max) {
            alert('El valor mínimo debe ser menor que el máximo');
            return;
        }

        // Llamar callback con todos los datos
        onSelectVariable(selectedVarIndex, selectedVariable.nb_var_idn, min, max);
        handleClose();
    };

    // 🚨 CREAR ALARMA
    const createAlarm = async () => {
        const fecha = new Date();
        const min = parseFloat(alarmMinValue);
        const max = parseFloat(alarmMaxValue);

        // Validaciones
        if (isNaN(min) || isNaN(max)) {
            toast.warning('Debe ingresar valores numéricos válidos');
            return;
        }

        if (min === 0 && max === 0) {
            toast.warning('Debe ingresar al menos un valor diferente de cero');
            return;
        }

        if (max <= min) {
            toast.warning('El valor máximo debe ser mayor al mínimo');
            return;
        }

        try {
            const dataSend = {
                nb_tldro_idn: parseInt(rig),
                nb_var_idn: parseInt(currentVarId),
                nb_usurio_idn: parseInt(uid),
                nb_alarm_min: min,
                nb_alarm_max: max,
                vr_adt_usureg: user || 'admin',
                dt_adt_fchreg: fecha.toISOString()
            };

            await insertTableBd('createalarm', dataSend);
            toast.success('✅ Alarma creada correctamente');
            
            // Llamar callback para refrescar datos ANTES de cerrar
            if (typeof onAlarmUpdated === 'function') {
                await onAlarmUpdated();
            }
            
            handleClose();
        } catch (error) {
            console.error('Error creando alarma:', error);
            toast.error('Error al crear la alarma');
        }
    };

    // 🚨 ACTUALIZAR ALARMA
    const updateAlarm = async () => {
        const fecha = new Date();
        const min = parseFloat(alarmMinValue);
        const max = parseFloat(alarmMaxValue);

        // Validaciones
        if (isNaN(min) || isNaN(max)) {
            toast.warning('Debe ingresar valores numéricos válidos');
            return;
        }

        if (min === 0 && max === 0) {
            toast.warning('Debe ingresar al menos un valor diferente de cero');
            return;
        }

        if (max <= min) {
            toast.warning('El valor máximo debe ser mayor al mínimo');
            return;
        }

        try {
            const dataSend = {
                nb_alarm_idn: parseInt(currentAlarmId),
                nb_alarm_min: min,
                nb_alarm_max: max,
                vr_adt_usureg: user || 'admin',
                dt_adt_fchreg: fecha.toISOString()
            };

            await updateTableBd('updatealarm', dataSend);
            toast.success('✅ Alarma actualizada correctamente');
            
            // Llamar callback para refrescar datos ANTES de cerrar
            if (typeof onAlarmUpdated === 'function') {
                await onAlarmUpdated();
            }
            
            handleClose();
        } catch (error) {
            console.error('Error actualizando alarma:', error);
            toast.error('Error al actualizar la alarma');
        }
    };

    // 🚨 MANEJAR APLICAR ALARMA
    const handleApplyAlarm = () => {
        const alarmExists = currentAlarmId !== null && currentAlarmId !== undefined;
        if (alarmExists) {
            updateAlarm();
        } else {
            createAlarm();
        }
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered dialogClassName="modal-themed-dialog">
            <Modal.Header closeButton>
                <Modal.Title>
                    ⚙️ Configurar Variable
                    {selectedVarIndex !== null && (
                        <small style={{ display: 'block', fontSize: '14px', marginTop: '4px' }}>
                            Posición: {selectedVarIndex + 1}
                            {currentVariableName && ` • Actual: ${currentVariableName}`}
                        </small>
                    )}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* TABS: Ejes o Variable */}
                <Tabs
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k)}
                    className="mb-3"
                    style={{ borderBottom: '2px solid #333' }}
                >
                    {/* TAB 1: AJUSTAR EJES (más común) */}
                    <Tab 
                        eventKey="axes" 
                        title={
                            <span style={{ fontSize: '15px' }}>
                                📊 Ajustar Ejes {selectedVariable && selectedVariable.nb_var_idn.toString() === currentVarId?.toString() && '(Misma variable)'}
                            </span>
                        }
                    >
                        <div style={{ padding: '16px 0' }}>
                            {/* Mostrar variable actual */}
                            <div style={{ 
                                padding: '12px',
                                backgroundColor: 'rgba(78, 205, 196, 0.1)',
                                borderRadius: '8px',
                                border: '1px solid rgba(78, 205, 196, 0.3)',
                                marginBottom: '16px'
                            }}>
                                <h6 style={{ color: '#4ECDC4', marginBottom: '8px', fontSize: '14px' }}>
                                    📌 Variable Actual: {selectedVariable?.vr_var_nom || 'No seleccionada'}
                                </h6>
                                <small style={{ color: '#999' }}>
                                    Solo ajustarás los límites de los ejes sin cambiar la variable
                                </small>
                            </div>

                            {/* Campos Min y Max */}
                            <div className="row g-3">
                                <div className="col-6">
                                    <Form.Group>
                                        <Form.Label style={{ fontSize: '14px', color: '#CCCCCC', fontWeight: '500' }}>
                                            📉 Valor Mínimo
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="any"
                                            value={minValue}
                                            onChange={(e) => setMinValue(e.target.value)}
                                            placeholder="Ej: 0"
                                            style={{ fontSize: '16px', fontWeight: 'bold' }}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-6">
                                    <Form.Group>
                                        <Form.Label style={{ fontSize: '14px', color: '#CCCCCC', fontWeight: '500' }}>
                                            📈 Valor Máximo
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="any"
                                            value={maxValue}
                                            onChange={(e) => setMaxValue(e.target.value)}
                                            placeholder="Ej: 100"
                                            style={{ fontSize: '16px', fontWeight: 'bold' }}
                                        />
                                    </Form.Group>
                                </div>
                            </div>
                            
                            {/* Info adicional */}
                            <div style={{ marginTop: '16px', padding: '10px', backgroundColor: '#0a0a0a', borderRadius: '6px' }}>
                                <small style={{ color: '#888' }}>
                                    💡 <strong>Tip:</strong> Ajusta estos valores para mejorar la visualización sin cambiar la variable monitoreada.
                                </small>
                            </div>
                        </div>
                    </Tab>

                    {/* TAB 2: CAMBIAR VARIABLE */}
                    <Tab 
                        eventKey="variable" 
                        title={<span style={{ fontSize: '15px' }}>🔄 Cambiar Variable</span>}
                    >
                        <div style={{ padding: '16px 0' }}>
                            {/* Input de búsqueda */}
                            <Form.Group className="mb-3">
                                <Form.Control
                                    type="text"
                                    placeholder="🔍 Buscar por nombre o unidad..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                {searchTerm && (
                                    <Form.Text style={{ color: '#4ECDC4' }}>
                                        {filteredVariables.length} resultado{filteredVariables.length !== 1 ? 's' : ''} encontrado{filteredVariables.length !== 1 ? 's' : ''}
                                    </Form.Text>
                                )}
                            </Form.Group>

                            {/* Tabla con scroll */}
                            <div style={{ maxHeight: '35vh', overflowY: 'auto', marginBottom: '16px' }}>
                    {filteredVariables && filteredVariables.length > 0 ? (
                        <Table striped bordered hover size="sm" className="mb-0">
                            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                <tr>
                                    <th style={{ width: '70%' }}>Variable</th>
                                    <th style={{ width: '30%' }}>Unidad</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredVariables.map(variable => {
                                    const isCurrentVar = currentVarId && variable.nb_var_idn.toString() === currentVarId.toString();
                                    const isSelected = selectedVariable && variable.nb_var_idn.toString() === selectedVariable.nb_var_idn.toString();
                                    
                                    return (
                                        <tr
                                            key={variable.nb_var_idn}
                                            onClick={() => handleVariableClick(variable)}
                                            style={{ 
                                                cursor: 'pointer',
                                                backgroundColor: isSelected ? 'rgba(78, 205, 196, 0.3)' : isCurrentVar ? 'rgba(78, 205, 196, 0.15)' : 'transparent',
                                                fontWeight: isSelected ? 'bold' : 'normal'
                                            }}
                                            title={`Seleccionar: ${variable.vr_var_nom}`}
                                        >
                                            <td className='text-start'>
                                                {isCurrentVar && '✓ '}{variable.vr_var_nom}
                                            </td>
                                            <td className='text-center'>{variable.vr_var_umd || '-'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    ) : searchTerm ? (
                        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666666' }}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
                            <div style={{ fontSize: '16px', marginBottom: '8px' }}>No se encontraron resultados</div>
                            <div style={{ fontSize: '14px' }}>Intenta con otro término de búsqueda</div>
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666666' }}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
                            <div style={{ fontSize: '16px', marginBottom: '8px' }}>Cargando variables...</div>
                            <div style={{ fontSize: '14px' }}>Por favor espera un momento</div>
                        </div>
                    )}
                </div>

                            {/* Campos Min y Max para nueva variable */}
                            {selectedVariable && (
                                <div style={{ 
                                    padding: '16px',
                                    backgroundColor: 'rgba(78, 205, 196, 0.1)',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(78, 205, 196, 0.3)'
                                }}>
                                    <h6 style={{ color: '#4ECDC4', marginBottom: '12px' }}>
                                        📊 Configurar Ejes: {selectedVariable.vr_var_nom}
                                    </h6>
                                    <div className="row g-3">
                                        <div className="col-6">
                                            <Form.Group>
                                                <Form.Label style={{ fontSize: '14px', color: '#CCCCCC' }}>
                                                    Valor Mínimo
                                                </Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    step="any"
                                                    value={minValue}
                                                    onChange={(e) => setMinValue(e.target.value)}
                                                    placeholder="Ej: 0"
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-6">
                                            <Form.Group>
                                                <Form.Label style={{ fontSize: '14px', color: '#CCCCCC' }}>
                                                    Valor Máximo
                                                </Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    step="any"
                                                    value={maxValue}
                                                    onChange={(e) => setMaxValue(e.target.value)}
                                                    placeholder="Ej: 100"
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                    <div style={{ marginTop: '12px' }}>
                                        <small style={{ color: '#888' }}>
                                            💡 Los ejes se ajustarán automáticamente para la nueva variable
                                        </small>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Tab>

                    {/* TAB 3: CONFIGURAR ALARMA 🚨 */}
                    <Tab 
                        eventKey="alarm" 
                        title={<span style={{ fontSize: '15px' }}>🚨 Configurar Alarma</span>}
                    >
                        <div style={{ padding: '16px 0' }}>
                            {/* Información de la variable actual */}
                            {currentVariableName && (
                                <div style={{ 
                                    padding: '12px 16px',
                                    backgroundColor: 'rgba(78, 205, 196, 0.1)',
                                    borderRadius: '8px',
                                    marginBottom: '16px',
                                    border: '1px solid rgba(78, 205, 196, 0.3)'
                                }}>
                                    <div style={{ fontSize: '14px', color: '#4ECDC4', fontWeight: 'bold' }}>
                                        📊 {currentVariableName}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                                        Posición: {selectedVarIndex + 1}
                                    </div>
                                </div>
                            )}

                            {/* Explicación de alarmas */}
                            <div style={{
                                padding: '12px 16px',
                                backgroundColor: 'rgba(255, 107, 107, 0.1)',
                                borderRadius: '8px',
                                marginBottom: '20px',
                                border: '1px solid rgba(255, 107, 107, 0.3)'
                            }}>
                                <div style={{ fontSize: '14px', color: '#ff6b6b', marginBottom: '8px' }}>
                                    <strong>🚨 ¿Cómo funcionan las alarmas?</strong>
                                </div>
                                <ul style={{ fontSize: '13px', color: '#cccccc', marginBottom: '0', paddingLeft: '20px' }}>
                                    <li>El valor se mostrará en <span style={{ color: '#ff6b6b', fontWeight: 'bold' }}>ROJO</span> cuando esté fuera del rango</li>
                                    <li>Valor en rojo = menor que mínimo O mayor que máximo</li>
                                    <li>Define un rango seguro para esta variable</li>
                                </ul>
                            </div>

                            {/* Formulario de rangos de alarma */}
                            <div style={{ 
                                padding: '16px',
                                backgroundColor: 'rgba(30, 30, 30, 0.5)',
                                borderRadius: '8px',
                                border: '1px solid #333'
                            }}>
                                <h6 style={{ color: '#ffffff', marginBottom: '16px', fontSize: '15px' }}>
                                    📏 Rango de Alarma
                                </h6>
                                
                                <div className="row g-3">
                                    <div className="col-6">
                                        <Form.Group>
                                            <Form.Label style={{ fontSize: '14px', color: '#CCCCCC' }}>
                                                🔻 Valor Mínimo
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="any"
                                                value={alarmMinValue}
                                                onChange={(e) => setAlarmMinValue(e.target.value)}
                                                placeholder="Ej: 0"
                                                style={{
                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                    border: '1px solid #444',
                                                    color: '#ffffff'
                                                }}
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-6">
                                        <Form.Group>
                                            <Form.Label style={{ fontSize: '14px', color: '#CCCCCC' }}>
                                                🔺 Valor Máximo
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="any"
                                                value={alarmMaxValue}
                                                onChange={(e) => setAlarmMaxValue(e.target.value)}
                                                placeholder="Ej: 100"
                                                style={{
                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                    border: '1px solid #444',
                                                    color: '#ffffff'
                                                }}
                                            />
                                        </Form.Group>
                                    </div>
                                </div>

                                {/* Vista previa del rango */}
                                {alarmMinValue && alarmMaxValue && parseFloat(alarmMinValue) < parseFloat(alarmMaxValue) && (
                                    <div style={{ marginTop: '16px' }}>
                                        <div style={{ fontSize: '13px', color: '#4ECDC4', marginBottom: '8px' }}>
                                            ✅ Rango configurado:
                                        </div>
                                        <div style={{
                                            padding: '8px 12px',
                                            backgroundColor: 'rgba(78, 205, 196, 0.1)',
                                            borderRadius: '4px',
                                            fontSize: '14px',
                                            color: '#ffffff',
                                            textAlign: 'center'
                                        }}>
                                            <span style={{ color: '#ff6b6b' }}>
                                                &lt; {alarmMinValue}
                                            </span>
                                            <span style={{ color: '#4ECDC4', margin: '0 12px' }}>
                                                → OK ({alarmMinValue} - {alarmMaxValue}) ←
                                            </span>
                                            <span style={{ color: '#ff6b6b' }}>
                                                &gt; {alarmMaxValue}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {/* Advertencia si el rango es inválido */}
                                {alarmMinValue && alarmMaxValue && parseFloat(alarmMinValue) >= parseFloat(alarmMaxValue) && (
                                    <div style={{ 
                                        marginTop: '12px',
                                        padding: '8px 12px',
                                        backgroundColor: 'rgba(255, 107, 107, 0.2)',
                                        borderRadius: '4px',
                                        color: '#ff6b6b',
                                        fontSize: '13px'
                                    }}>
                                        ⚠️ El valor mínimo debe ser menor que el máximo
                                    </div>
                                )}

                                {/* Estado de la alarma */}
                                <div style={{ marginTop: '16px', fontSize: '13px', color: '#888' }}>
                                    {currentAlarmId ? (
                                        <>
                                            <span style={{ color: '#4ECDC4' }}>📝</span> Esta variable ya tiene una alarma configurada
                                        </>
                                    ) : (
                                        <>
                                            <span style={{ color: '#ffcc00' }}>➕</span> Esta variable no tiene alarma configurada
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Tab>
                </Tabs>
            </Modal.Body>
            <Modal.Footer style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {/* Info de qué se va a hacer */}
                <div style={{ flex: 1, textAlign: 'left' }}>
                    {activeTab === 'alarm' ? (
                        <small style={{ color: '#ff6b6b', fontSize: '13px' }}>
                            🚨 {currentAlarmId ? 'Actualiza' : 'Configura'} el rango de alarma
                        </small>
                    ) : selectedVariable && (
                        <small style={{ fontSize: '13px' }}>
                            {selectedVariable.nb_var_idn.toString() === currentVarId?.toString() ? (
                                <>📊 Solo se actualizarán los ejes</>
                            ) : (
                                <>🔄 Se cambiará la variable y sus ejes</>
                            )}
                        </small>
                    )}
                </div>
                
                {/* Botones */}
                <div style={{ display: 'flex', gap: '10px' }}>
                    <Button 
                        variant="secondary" 
                        onClick={handleClose}
                        style={{ 
                            backgroundColor: '#555555',
                            border: 'none',
                            padding: '8px 20px'
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button 
                        variant="primary"
                        onClick={activeTab === 'alarm' ? handleApplyAlarm : handleApply}
                        disabled={activeTab !== 'alarm' && !selectedVariable}
                        style={{ 
                            backgroundColor: activeTab === 'alarm' ? '#ff6b6b' : '#4ECDC4',
                            border: 'none',
                            padding: '8px 20px',
                            opacity: (activeTab === 'alarm' || selectedVariable) ? 1 : 0.5,
                            cursor: (activeTab === 'alarm' || selectedVariable) ? 'pointer' : 'not-allowed'
                        }}
                    >
                        {activeTab === 'alarm' ? (
                            currentAlarmId ? '✓ Actualizar Alarma' : '✓ Crear Alarma'
                        ) : selectedVariable && selectedVariable.nb_var_idn.toString() === currentVarId?.toString() ? (
                            <>✓ Aplicar Ejes</>
                        ) : (
                            <>✓ Aplicar Cambios</>
                        )}
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalVarSelectorOpt;
